/**
 * AuthLayout - Glass morphism wrapper for auth pages
 */

import { type ReactNode } from 'react';
import { motion } from 'framer-motion';
import { cn } from '@/utils/cn';

interface AuthLayoutProps {
  children: ReactNode;
  title: string;
  subtitle?: string;
}

export function AuthLayout({ children, title, subtitle }: AuthLayoutProps) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-hud-primary/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-hud-secondary/10 rounded-full blur-3xl animate-pulse delay-1000" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className={cn(
          'relative w-full max-w-md',
          'bg-glass-bg backdrop-blur-xl',
          'border border-glass-border',
          'rounded-2xl',
          'shadow-lg shadow-black/20',
          'p-8'
        )}
      >
        {/* Header */}
        <div className="text-center mb-8">
          {/* Logo */}
          <motion.div
            initial={{ scale: 0.8 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2 }}
            className="inline-flex items-center gap-3 mb-4"
          >
            <img
              src="/logo.png"
              alt="Blurp.ie"
              className="h-12 w-auto"
            />
          </motion.div>

          <h1 className="text-xl font-semibold text-white mb-2">{title}</h1>
          {subtitle && (
            <p className="text-text-secondary text-sm">{subtitle}</p>
          )}
        </div>

        {/* Content */}
        {children}
      </motion.div>
    </div>
  );
}
